import { AnyKindOfDictionary } from 'lodash';
import { compose, merge, get, reduce } from 'lodash/fp';
import * as yup from 'yup';
import logger from 'Utils/Logger';

import { AuthError } from 'aws-amplify/auth';

const composeFormErrors = compose(
  reduce(
    (errors: { [x: string]: string }, validationError: yup.ValidationError) =>
      merge(errors, {
        [validationError.path || 'no path']: validationErrorCodeMessageSanitizer(
          validationError.message,
        ),
      }),
    {},
  ),
  get('inner'),
);

const invalidFieldNames = (validationError: unknown): string[] =>
  ((validationError as { inner?: yup.ValidationError[] } | null | undefined)?.inner ?? []).map(
    (fieldError) => fieldError.path || 'no path',
  );

const ValidateForm = (
  formSchema: yup.ObjectSchema<yup.AnyObject, unknown, unknown, ''>,
  values: AnyKindOfDictionary,
) => {
  return (
    formSchema
      .validate(values, { abortEarly: false })
      // Resolve to nothing. What validation hands back is what the user typed,
      // a password among it, so it is neither logged nor returned.
      .then(() => undefined)
      .catch((err) => {
        // Field names only: the error itself carries the typed values.
        logger.warn('Form validation failed for:', invalidFieldNames(err));
        return composeFormErrors(err);
      })
  );
};

const validationErrorCodeMessageSanitizer = (message: string) => {
  if (message.includes('must be a `number` type')) {
    return 'Value must be a number';
  }
  return message;
};
// It appears an error code can be used for multiple types of errors, so we should check
// them based on both code and message
const AuthErrorCodeMessageMap: { [key: string]: string } = {
  [['UsernameExistsException', 'hello world'].toString()]:
    'An account with that email already exists.',
  [[
    'InvalidPasswordException',
    'Password did not conform with policy: Password not long enough',
  ].toString()]: 'Password did not conform with policy: Password not long enough',
  [['NotAuthorizedException', 'Incorrect username or password.'].toString()]:
    'Incorrect username or password.',
  [['NotAuthorizedException', 'Password attempts exceeded'].toString()]:
    'Password attempts exceeded.',
  [['LimitExceededException', 'Attempt limit exceeded, please try after some time.'].toString()]:
    'Attempt limit exceeded, please try after some time.',
  [['CodeMismatchException', 'Invalid verification code provided, please try again.'].toString()]:
    'Invalid verification code provided. If this continues, try generating a new one.',
  [['UsernameExistsException', 'An account with the given email already exists.'].toString()]:
    'An account with the given email already exists. Try resetting your password.',
  [[
    'UsernameExistsException',
    'An account with the given phone_number already exists.',
  ].toString()]: 'An account with that phone number already exists. Try resetting your password.',
  [['UserNotConfirmedException', 'User is not confirmed.'].toString()]:
    "Your account hasn't yet been verified. Please check your phone or email for a verification code.",
  [['ExpiredCodeException', 'Invalid code provided, please request a code again.'].toString()]:
    'Invalid code provided, please request a code again. If you have not yet verified, you must first do so.',
  [['NotAuthorizedException', 'User cannot be confirmed. Current status is CONFIRMED'].toString()]:
    'You are already confirmed. Proceed to login.',
  [['AliasExistsException', 'An account with the given phone_number already exists.'].toString()]:
    'An account with that phone number already exists',
  [['AliasExistsException', 'An account with the given email already exists.'].toString()]:
    'An account with that email already exists',
  [['UserUnAuthenticatedException', 'User needs to be authenticated to call this API.'].toString()]:
    'Email or phone number is unverified. Please verify your account.',
};

const AuthErrorCodeInputLabelMap: {
  [key: string]: string[];
} = {
  UsernameExistsException: ['emailOrPhone'],
  // "code" used only for verification link page
  NotAuthorizedException: ['password', 'repeatPassword', 'code'],
  LimitExceededException: ['emailOrPhone', 'code', 'password', 'repeatPassword'],
  CodeMismatchException: ['code'],
  ExpiredCodeException: ['code', 'password', 'repeatPassword'],
  UserNotConfirmedException: ['emailOrPhone'],
  InvalidPasswordException: ['password', 'repeatPassword'],
  AliasExistsException: ['emailOrPhone', 'code'],
  UserUnAuthenticatedException: ['emailOrPhone'],
};

// This might take in a general "Error" if it is used wrong,
// so each usage of "err.code" is protected with "?". Unfortunately,
// Typescript doesn't like "AmplifyAuthError | Error"
const handleAuthErrors = (error: AuthError): Record<string, string> => {
  let errorLabels, errorMessage;
  if (
    !AuthErrorCodeInputLabelMap[error?.name] ||
    !AuthErrorCodeMessageMap[[error?.name, error?.message].toString()]
  ) {
    console.error('Unknown error:', error, error.name, error.message);
    errorLabels = ['unknown'];
    errorMessage = 'Unknown Error';
  } else {
    errorLabels = AuthErrorCodeInputLabelMap[error?.name];
    errorMessage =
      AuthErrorCodeMessageMap[[error?.name, error?.message].toString()] || 'Unkown Error';
  }
  const newErrObj: { [key: string]: string } = {};
  for (const errorLabel of errorLabels) {
    newErrObj[errorLabel] = errorMessage;
  }
  return newErrObj;
};

// Sign-up was refused because an account already uses that email or phone
// number. Keyed on the error name alone: the message wording varies with how
// the address was registered.
const isAccountExistsError = (error: unknown): boolean =>
  (error as { name?: unknown } | null | undefined)?.name === 'UsernameExistsException';

export {
  composeFormErrors,
  ValidateForm,
  // AuthErrorCodeMessageMap,
  handleAuthErrors,
  isAccountExistsError,
};
